import { useNavigate } from "react-router";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Badge } from "../../../components/shared/Badge";
import { usePlatformEnterprise } from "@platform/hooks";
import { rootDomain, enterpriseSubdomainUrl } from "../../../lib/subdomain";

interface EnterprisePageShellProps {
  enterpriseId: string;
  pageName: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}

export function EnterprisePageShell({ enterpriseId, pageName, children, action }: EnterprisePageShellProps) {
  const navigate = useNavigate();
  const { data: enterprise, isLoading } = usePlatformEnterprise(enterpriseId);
  const ent = enterprise as { id: string; name: string; slug: string; status: string } | undefined;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!ent) {
    return (
      <div className="p-8 text-center text-sm text-ds-text-muted">
        Enterprise not found.{" "}
        <button onClick={() => navigate("/platform/enterprises")} className="text-indigo-600 hover:underline">
          Back to enterprises
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <button
        onClick={() => navigate("/platform/enterprises")}
        className="flex items-center gap-1.5 text-sm text-ds-text-secondary hover:text-ds-text transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back to enterprises
      </button>

      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
            {ent.name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h1 className="text-lg font-semibold text-ds-text">{pageName}</h1>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="text-sm text-indigo-600">{ent.name}</span>
              <span className="text-ds-text-muted text-sm">·</span>
              <span className="text-sm text-ds-text-muted">{ent.slug}.{rootDomain()}</span>
              <Badge variant={ent.status === "active" ? "green" : ent.status === "suspended" ? "amber" : "gray"}>
                {ent.status}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {action}
          <a
            href={enterpriseSubdomainUrl(ent.slug, "/dashboard")}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-ds-bg border border-ds-border text-sm text-ds-text-secondary hover:bg-white transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open workspace
          </a>
        </div>
      </div>

      {children}
    </div>
  );
}
